import type { ReactNode } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import type { ContextPanelScope } from './context-panel-context';
import { useContextPanel } from './context-panel-context';

export interface ContextPanelSlotProps {
  /**
   * The panel body content.
   */
  children: ReactNode;
  /**
   * When true, the panel skips its slide-out animation on close and
   * disappears immediately. See `ContextPanelOpenOptions.exitImmediate`
   * for context.
   *
   * @default false
   */
  exitImmediate?: boolean;
  /**
   * Custom header content. Use `ContextPanelHeader` for a consistent look.
   */
  header: ReactNode;
  /**
   * Whether to apply default padding to the body content.
   *
   * @default true
   */
  padded?: boolean;
  /**
   * When true, the panel cannot be closed by the user.
   */
  persistent?: boolean;
  /**
   * The panel scope to target.
   *
   * @default 'component'
   */
  scope?: ContextPanelScope;
  /**
   * Override the default panel width.
   */
  width?: string;
}

/**
 * A declarative component that opens the context panel when mounted and closes
 * it when unmounted. This is a convenience wrapper around `useContextPanel()`
 * for cases where the panel content is known at render time, avoiding the need
 * for imperative `open()`/`close()` calls.
 *
 * Unlike the hook defaults approach, the Slot is reactive to prop changes —
 * updating `children` or `header` will update the panel content immediately.
 *
 * @example
 * <ContextPanel.Slot scope="page" header={<ContextPanel.Header>Recent Activity</ContextPanel.Header>} persistent>
 *   <ActivityFeed />
 * </ContextPanel.Slot>
 */
export const ContextPanelSlot = ({
  children,
  exitImmediate,
  header,
  padded,
  persistent,
  scope,
  width,
}: ContextPanelSlotProps) => {
  const { open } = useContextPanel(scope);

  /**
   * Layout-effect timing keeps the panel's open/close in step with the
   * slot's mount/unmount in a single paint frame; deferred-effect
   * timing showed as a one-frame stutter on tab navigation.
   */
  useIsomorphicLayoutEffect(() => {
    open({
      content: children,
      exitImmediate,
      header,
      padded,
      persistent,
      width,
    });
  }, [children, exitImmediate, header, padded, persistent, width]);

  return null;
};

export default ContextPanelSlot;
